/**
 * VariationsSection Component
 * Displays and manages project variations (additions/deductions)
 */
import { useState } from 'react';
import { format } from 'date-fns';
import { Receipt, ChevronRight, ChevronDown, Plus, Trash2, TrendingUp, TrendingDown, Image, Film, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

// Variation status colors
export const VARIATION_STATUS_COLORS = {
  'Pending Approval': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  'Approved': 'bg-green-500/20 text-green-400 border-green-500/30',
  'Rejected': 'bg-red-500/20 text-red-400 border-red-500/30'
};

// Format currency helper
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
};

export function VariationsSection({ 
  variations, 
  projectId, 
  api, 
  onVariationsChange,
  canEdit = false,
  canDelete = false,
  isVisible = true 
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [variationForm, setVariationForm] = useState({
    description: '',
    amount: '',
    type: 'addition',
    notes: ''
  });

  const handleAddVariation = async () => {
    if (!variationForm.description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    if (!variationForm.amount) {
      toast.error('Please enter an amount');
      return;
    }
    
    setSubmitting(true);
    try {
      const amount = parseFloat(variationForm.amount);
      const finalAmount = variationForm.type === 'deduction' ? -Math.abs(amount) : Math.abs(amount);
      
      const response = await api.post(`/projects/${projectId}/variations`, {
        description: variationForm.description,
        amount: finalAmount,
        notes: variationForm.notes || null
      });
      
      if (onVariationsChange) {
        onVariationsChange([...variations, response.data]);
      }
      
      setVariationForm({ description: '', amount: '', type: 'addition', notes: '' });
      setDialogOpen(false);
      toast.success('Variation added');
    } catch (error) {
      console.error('Failed to add variation:', error);
      toast.error('Failed to add variation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (variationId, newStatus) => {
    try {
      await api.put(`/projects/${projectId}/variations/${variationId}`, { status: newStatus });
      
      if (onVariationsChange) {
        onVariationsChange(variations.map(v => 
          v.id === variationId ? { ...v, status: newStatus } : v
        ));
      }
      
      toast.success(`Variation ${newStatus.toLowerCase()}`);
    } catch (error) {
      console.error('Failed to update variation:', error);
      toast.error('Failed to update variation');
    }
  };

  const handleDeleteVariation = async (variationId) => {
    if (!confirm('Delete this variation?')) return;
    
    try {
      await api.delete(`/projects/${projectId}/variations/${variationId}`);
      
      if (onVariationsChange) {
        onVariationsChange(variations.filter(v => v.id !== variationId));
      }
      
      toast.success('Variation deleted');
    } catch (error) {
      console.error('Failed to delete variation:', error);
      toast.error('Failed to delete variation');
    }
  };

  if (!isVisible) return null;

  // Calculate summary values
  const additions = variations.filter(v => v.amount > 0 && v.status === 'Approved').reduce((s, v) => s + v.amount, 0);
  const deductions = variations.filter(v => v.amount < 0 && v.status === 'Approved').reduce((s, v) => s + v.amount, 0);
  const pending = variations.filter(v => v.status === 'Pending Approval').length;

  return (
    <>
      <div>
        <div 
          className="flex items-center justify-between mb-4 cursor-pointer"
          onClick={() => setCollapsed(!collapsed)}
        >
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Receipt className="w-5 h-5 inline-block mr-2 text-[#F5A623]" />
              Variations ({variations.length})
            </h2>
            {collapsed ? (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
          {!collapsed && canEdit && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setDialogOpen(true);
              }}
              data-testid="add-variation-btn"
              className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Variation
            </Button>
          )}
        </div>

        {!collapsed && (
          <>
            {/* Variation Summary */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <Card className="bg-[#2A3240] border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                    <p className="text-xs text-gray-500">Additions</p>
                  </div>
                  <p className="text-xl font-bold text-green-400">{formatCurrency(additions)}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#2A3240] border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="w-4 h-4 text-red-400" />
                    <p className="text-xs text-gray-500">Deductions</p>
                  </div>
                  <p className="text-xl font-bold text-red-400">{formatCurrency(deductions)}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#2A3240] border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Receipt className="w-4 h-4 text-yellow-400" />
                    <p className="text-xs text-gray-500">Pending</p>
                  </div>
                  <p className="text-xl font-bold text-yellow-400">{pending}</p>
                </CardContent>
              </Card>
            </div>

            {variations.length === 0 ? (
              <Card className="bg-[#2A3240] border-white/10">
                <CardContent className="py-8 text-center">
                  <Receipt className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                  <p className="text-gray-400">No variations yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {variations.map((variation) => (
                  <Card key={variation.id} className="bg-[#2A3240] border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={VARIATION_STATUS_COLORS[variation.status]}>
                              {variation.status}
                            </Badge>
                            <span className={`text-lg font-bold ${variation.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {variation.amount >= 0 ? '+' : ''}{formatCurrency(variation.amount)}
                            </span>
                          </div>
                          <p className="text-white font-medium">{variation.description}</p>
                          {variation.notes && (
                            <p className="text-sm text-gray-400 mt-1">{variation.notes}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-2">
                            {variation.created_by_name} • {format(new Date(variation.date), 'dd MMM yyyy')}
                          </p>
                          {variation.media?.length > 0 && (
                            <div className="flex gap-2 mt-2">
                              {variation.media.map((m, i) => {
                                const isVideo = m.file_type === 'video' || m.file_type?.startsWith('video');
                                return (
                                  <div key={m.file_id || m.url || m.filename || i} className="w-12 h-12 rounded bg-[#1E2530] flex items-center justify-center">
                                    {isVideo ? (
                                      <Film className="w-5 h-5 text-purple-400" />
                                    ) : (
                                      <Image className="w-5 h-5 text-gray-500" />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {canEdit && variation.status === 'Pending Approval' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleUpdateStatus(variation.id, 'Approved')}
                                className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateStatus(variation.id, 'Rejected')}
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                              >
                                Reject
                              </Button>
                            </>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteVariation(variation.id)}
                              className="text-gray-500 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Variation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Add Variation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Type</Label>
              <Select value={variationForm.type} onValueChange={(v) => setVariationForm(prev => ({ ...prev, type: v }))}>
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  <SelectItem value="addition" className="text-white">Addition (+)</SelectItem>
                  <SelectItem value="deduction" className="text-white">Deduction (-)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Description *</Label>
              <Input
                value={variationForm.description}
                onChange={(e) => setVariationForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Additional electrical work"
                className="bg-[#1E2530] border-white/20 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Amount (£) *</Label>
              <Input
                type="number"
                step="0.01"
                value={variationForm.amount}
                onChange={(e) => setVariationForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
                className="bg-[#1E2530] border-white/20 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Notes</Label>
              <Textarea
                value={variationForm.notes}
                onChange={(e) => setVariationForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional details..."
                rows={3}
                className="bg-[#1E2530] border-white/20 text-white"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 border-white/20 text-gray-300">
                Cancel
              </Button>
              <Button onClick={handleAddVariation} disabled={submitting} className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Variation'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
